/**
 * Wishlist ("Merkliste") store — the single source of truth.
 *
 * localStorage holds a JSON array of event snapshots keyed by the STABLE natural
 * key `post_id + date_start`. The event_date row `id` is regenerated on every
 * event save (Repository::replacePostRows inserts new rows then deletes the old),
 * so it can't be the key. Each snapshot is a last-known cache for instant,
 * offline-friendly render; src/js/wishlist.js reconciles it against live data via
 * the glunz/v1/event-dates/lookup endpoint.
 *
 * Pure module — imports nothing local, so calendarHelpers.js (which marks/toggles
 * hearts) and wishlist.js (the panel) can both depend on it without a cycle.
 */

const STORAGE_KEY = 'wishlist:items'

const listeners = new Set()

// `${post_id}__${date_start}`. Accepts a REST/calendar row (post_id, date_start)
// or a stored snapshot (postId, dateStart) — same key either way.
export const keyFor = (row) => {
  const postId = row.post_id ?? row.postId
  const dateStart = row.date_start ?? row.dateStart
  return `${postId}__${dateStart}`
}

const read = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY))
    return Array.isArray(raw) ? raw : []
  } catch {
    return []
  }
}

const notify = () => {
  for (const cb of listeners) cb()
}

const write = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // Storage full or disabled (private mode) — stay functional for this page load.
  }
  notify()
}

const byDate = (a, b) => String(a.dateStart).localeCompare(String(b.dateStart))

export const getItems = () => read().sort(byDate)

export const has = (key) => read().some((i) => i.key === key)

// Add if absent, remove if present. Returns the NEW membership (true = now saved).
export const toggle = (snapshot) => {
  const items = read()
  const idx = items.findIndex((i) => i.key === snapshot.key)
  if (idx === -1) {
    items.push(snapshot)
    write(items)
    return true
  }
  items.splice(idx, 1)
  write(items)
  return false
}

export const remove = (key) => write(read().filter((i) => i.key !== key))

export const clear = () => write([])

// Persist reconciled snapshots (see wishlist.js reconcile()). Replaces the list.
export const setItems = (items) => write(items)

export const subscribe = (cb) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

// Another tab changed the wishlist → re-render here too.
window.addEventListener('storage', (e) => {
  if (e.key === STORAGE_KEY) notify()
})

export default { keyFor, getItems, has, toggle, remove, clear, setItems, subscribe }
